import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule }  from '@angular/forms';
import { DataService } from '../../services/data.service';

// ──────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────
export type BookStatus    = 'Available' | 'Issued' | 'Reserved' | 'Lost' | 'Damaged' | 'Under Repair';
export type MemberType    = 'Student' | 'Staff' | 'Faculty';
export type IssueStatus   = 'Issued' | 'Returned' | 'Overdue' | 'Lost';
export type LibTab        = 'catalog' | 'issue' | 'records' | 'members';
export type ViewMode      = 'list' | 'form' | 'detail';

export interface Book {
  id:               number;
  accession_no:     string;
  isbn:             string;
  title:            string;
  author:           string;
  publisher:        string;
  publication_year: number | null;
  edition:          string;
  category:         string;
  subject:          string;
  language:         string;
  pages:            number | null;
  price:            number | null;
  copies_total:     number;
  copies_available: number;
  shelf_location:   string;
  description:      string;
  cover_image:      string;
  status:           BookStatus;
  added_on:         string;
}

export interface LibMember {
  id:               number;
  member_id:        string;
  member_name:      string;
  member_type:      MemberType;
  class_dept:       string;
  mobile:           string;
  email:            string;
  photo:            string;
  max_books:        number;
  active_issues:    number;
  fine_pending:     number;
  membership_from:  string;
  membership_to:    string;
  is_active:        boolean;
}

export interface IssueRecord {
  id:               number;
  book_id:          number;
  book_title:       string;
  accession_no:     string;
  member_id:        number;
  member_name:      string;
  member_code:      string;
  class_dept:       string;
  issue_date:       string;
  due_date:         string;
  return_date:      string;
  fine_per_day:     number;
  fine_amount:      number;
  fine_paid:        boolean;
  status:           IssueStatus;
  remarks:          string;
}

// Issue form
export interface IssueForm {
  member_search:    string;
  book_search:      string;
  selected_member:  LibMember | null;
  selected_book:    Book | null;
  issue_date:       string;
  due_date:         string;
  remarks:          string;
}

// Return form
export interface ReturnForm {
  record_id:        number | null;
  return_date:      string;
  fine_amount:      number;
  fine_paid:        boolean;
  remarks:          string;
  selected_record:  IssueRecord | null;
}

@Component({
  selector:    'app-library',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './library.html',
  styleUrl:    './library.scss',
})
export class Library implements OnInit {
  dataService = inject(DataService);
  loading = false;

  activeTab        = signal<LibTab>('catalog');
  saveSuccess      = signal('');
  showDeleteModal  = signal(false);
  deleteCtx        = signal<{ label: string; id: number; type: string } | null>(null);

  // Book catalog state
  bookMode         = signal<ViewMode>('list');
  editingBookId    = signal<number | null>(null);
  bookSubmitted    = false;
  bookForm: Partial<Book> = {};
  bookSearch       = '';
  bookCategory     = '';
  bookStatus       = '';

  // Issue / Return state
  issueMode        = signal<'issue' | 'return'>('issue');
  issueForm: IssueForm = this.blankIssueForm();
  issueSubmitted   = false;
  returnForm: ReturnForm = this.blankReturnForm();
  returnSearch     = '';

  // Records state
  recSearch        = '';
  recStatus        = '';
  recDateFrom      = '';
  recDateTo        = '';

  // Members state
  memberMode       = signal<ViewMode>('list');
  editingMemberId  = signal<number | null>(null);
  memberForm: Partial<LibMember> = {};
  memberSubmitted  = false;
  memberSearch     = '';

  // ── LOOKUPS ──────────────────────────────────────────
  readonly categories  = ['Fiction','Non-Fiction','Science','Mathematics','History','Geography','Reference','Magazine','Encyclopedia','Biography','Technology','Arts','Health','Law','Economics','Other'];
  readonly languages   = ['English','Hindi','Sanskrit','Urdu','Bengali','Tamil','Other'];
  readonly memberTypes: MemberType[] = ['Student','Staff','Faculty'];
  readonly classOptions= this.dataService.classes;

  private nextBookId   = 13;
  private nextMemberId = 9;
  private nextIssueId  = 9;

  // ── DATA ─────────────────────────────────────────────
  books   = signal<Book[]>([]);
  members = signal<LibMember[]>([]);
  records = signal<IssueRecord[]>([]);

  // ── COMPUTED ─────────────────────────────────────────
  // readonly filteredBooks = computed(() => {
  //   const q = this.bookSearch.toLowerCase();
  //   return this.books().filter(b =>
  //     (!q || [b.title,b.author,b.accession_no,b.isbn,b.publisher].some(v => v.toLowerCase().includes(q))) &&
  //     (!this.bookCategory || b.category === this.bookCategory) &&
  //     (!this.bookStatus   || b.status   === this.bookStatus)
  //   );
  // });

  filteredBooks(): any[] {
    const q = this.bookSearch ? this.bookSearch.toLowerCase() : '';
    
    return this.books().filter(b =>
      // 1. Search Query Filter (Safely handles numbers and nulls)
      (!q || [b.title, b.author, b.accession_no, b.isbn, b.publisher].some(v => 
        v ? String(v).toLowerCase().includes(q) : false
      )) &&
      // 2. Category Filter
      (!this.bookCategory || b.category === this.bookCategory) &&
      // 3. Status Filter
      (!this.bookStatus || b.status === this.bookStatus)
    );
  }

  // readonly filteredRecords = computed(() => {
  //   const q = this.recSearch.toLowerCase();
  //   return this.records().filter(r =>
  //     (!q || [r.book_title,r.member_name,r.member_code,r.accession_no].some(v => v.toLowerCase().includes(q))) &&
  //     (!this.recStatus   || r.status === this.recStatus) &&
  //     (!this.recDateFrom || r.issue_date >= this.recDateFrom) &&
  //     (!this.recDateTo   || r.issue_date <= this.recDateTo)
  //   ).sort((a,b) => b.issue_date.localeCompare(a.issue_date));
  // });

  // Define it as a regular class function
  filteredRecords(): any[] {
    // 1. Get the current search query
    const q = this.recSearch ? this.recSearch.toLowerCase() : '';
    
    // 2. Filter and sort the raw array data
    return this.records() // assuming records is still a signal, otherwise use this.records
      .filter(r => 
        (!q || [r.book_title, r.member_name, r.member_code, r.accession_no].some(v => 
          v ? String(v).toLowerCase().includes(q) : false
        )) && 
        (!this.recStatus || r.status === this.recStatus) && 
        (!this.recDateFrom || r.issue_date >= this.recDateFrom) && 
        (!this.recDateTo || r.issue_date <= this.recDateTo)
      )
      .sort((a, b) => b.issue_date.localeCompare(a.issue_date));
  }

  // readonly filteredMembers = computed(() => {
  //   const q = this.memberSearch.toLowerCase();
  //   return this.members().filter(m =>
  //     !q || [m.member_name,m.member_id,m.class_dept,m.mobile].some(v => v.toLowerCase().includes(q))
  //   );
  // });

  filteredMembers(): any[] {
    const q = this.memberSearch ? this.memberSearch.toLowerCase() : '';
    
    return this.members().filter(m =>
      // Safely converts numbers/nulls to string before checking includes
      !q || [m.member_name, m.member_id, m.class_dept, m.mobile].some(v => 
        v ? String(v).toLowerCase().includes(q) : false
      )
    );
  }


  readonly overdueIssues = computed(() =>
    this.records().filter(r => r.status === 'Issued' && new Date(r.due_date) < new Date())
  );

  readonly returnableRecords = computed(() => {
    const q = this.returnSearch.toLowerCase();
    return this.records().filter(r =>
      r.status === 'Issued' &&
      (!q || [r.book_title,r.member_name,r.member_code].some(v => v.toLowerCase().includes(q)))
    );
  });

  readonly libStats = computed(() => {
    const b = this.books();
    const r = this.records();
    return {
      totalBooks:     b.reduce((s,x) => s + x.copies_total, 0),
      available:      b.reduce((s,x) => s + x.copies_available, 0),
      issued:         r.filter(x => x.status === 'Issued').length,
      overdue:        this.overdueIssues().length,
      members:        this.members().filter(m => m.is_active).length,
      fineCollected:  r.filter(x => x.fine_paid).reduce((s,x) => s + x.fine_amount, 0),
      finePending:    r.filter(x => !x.fine_paid && x.fine_amount > 0).reduce((s,x) => s + x.fine_amount, 0),
    };
  });

  ngOnInit() {
    this.loading = true;
    this.dataService.getAllRecords('lib_books').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.books.set(res);
      }
    });

    this.loading = true;
    this.dataService.getAllRecords('lib_members').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.members.set(res);
      }
    });

    this.loading = true;
    this.dataService.getAllRecords('lib_issue_rec').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.records.set(res);
      }
    });

  }

  // ── BOOK CRUD ─────────────────────────────────────────
  openNewBook(): void {
    this.bookForm = { status:'Available', copies_total:1, copies_available:1, language:'English', publication_year: new Date().getFullYear() };
    this.editingBookId.set(null); this.bookSubmitted = false;
    this.bookMode.set('form');
  }

  editBook(b: Book): void {
    this.bookForm = { ...b };
    this.editingBookId.set(b.id); this.bookSubmitted = false;
    this.bookMode.set('form');
  }

  viewBook(b: Book): void { this.editingBookId.set(b.id); this.bookMode.set('detail'); }

  saveBook(): void {
    this.bookSubmitted = true;
    if (!this.bookForm.title || !this.bookForm.author || !this.bookForm.accession_no) return;
    const now = new Date().toISOString().substring(0,10);
    const book: Book = {
      id:               this.editingBookId() ?? this.nextBookId++,
      accession_no:     this.bookForm.accession_no!,
      isbn:             this.bookForm.isbn ?? '',
      title:            this.bookForm.title!,
      author:           this.bookForm.author!,
      publisher:        this.bookForm.publisher ?? '',
      publication_year: this.bookForm.publication_year ?? null,
      edition:          this.bookForm.edition ?? '',
      category:         this.bookForm.category ?? 'Other',
      subject:          this.bookForm.subject ?? '',
      language:         this.bookForm.language ?? 'English',
      pages:            this.bookForm.pages ?? null,
      price:            this.bookForm.price ?? null,
      copies_total:     this.bookForm.copies_total ?? 1,
      copies_available: this.bookForm.copies_available ?? this.bookForm.copies_total ?? 1,
      shelf_location:   this.bookForm.shelf_location ?? '',
      description:      this.bookForm.description ?? '',
      cover_image:      this.bookForm.cover_image ?? '',
      status:           this.bookForm.status ?? 'Available',
      added_on:         now,
    };
    if (this.editingBookId() !== null) {
      this.doUpdate('lib_books', book);
      this.books.update(l => l.map(b => b.id === book.id ? book : b));
    } else {
      this.doSave('lib_books', book);
      this.books.update(l => [...l, book]);
    }

    this.bookMode.set('list'); this.flash('Book saved!');
  }

  // ── ISSUE BOOK ────────────────────────────────────────
  searchMember(): void {
    const q = this.issueForm.member_search.trim().toUpperCase();
    const m = this.members().find(m =>
      m.member_id.toUpperCase() === q ||
      m.member_name.toLowerCase().includes(q.toLowerCase())
    );
    this.issueForm.selected_member = m ?? null;
  }

  searchBook(): void {
    const q = this.issueForm.book_search.trim().toLowerCase();
    const b = this.books().find(b =>
      b.accession_no.toLowerCase() === q ||
      b.isbn.toLowerCase() === q ||
      b.title.toLowerCase().includes(q)
    );
    this.issueForm.selected_book = (b && b.copies_available > 0) ? b : null;
  }

  issueBook(): void {
    this.issueSubmitted = true;
    const m = this.issueForm.selected_member;
    const b = this.issueForm.selected_book;
    if (!m || !b || !this.issueForm.issue_date || !this.issueForm.due_date) return;
    if (m.active_issues >= m.max_books) { alert(`${m.member_name} has reached the maximum issue limit (${m.max_books} books).`); return; }

    const record: IssueRecord = {
      id: this.nextIssueId++,
      book_id: b.id, book_title: b.title, accession_no: b.accession_no,
      member_id: m.id, member_name: m.member_name, member_code: m.member_id, class_dept: m.class_dept,
      issue_date: this.issueForm.issue_date, due_date: this.issueForm.due_date,
      return_date: '', fine_per_day: 2, fine_amount: 0, fine_paid: false,
      status: 'Issued', remarks: this.issueForm.remarks,
    };

    this.doSave('lib_issue_rec', record);

    this.records.update(l => [record, ...l]);
    this.books.update(l => l.map(bk => bk.id === b.id
      ? { ...bk, copies_available: bk.copies_available - 1, status: bk.copies_available - 1 === 0 ? 'Issued' : 'Available' }
      : bk
    ));
    this.members.update(l => l.map(mb => mb.id === m.id
      ? { ...mb, active_issues: mb.active_issues + 1 }
      : mb
    ));
    this.issueForm = this.blankIssueForm(); this.issueSubmitted = false;
    this.flash(`"${b.title}" issued to ${m.member_name}!`);
  }

  // ── RETURN BOOK ───────────────────────────────────────
  selectReturn(r: IssueRecord): void {
    const today = new Date().toISOString().substring(0,10);
    const due   = new Date(r.due_date);
    const ret   = new Date(today);
    const overdueDays = Math.max(0, Math.ceil((ret.getTime() - due.getTime()) / 86400000));
    const fine  = overdueDays * r.fine_per_day;
    this.returnForm = { record_id: r.id, return_date: today, fine_amount: fine, fine_paid: fine === 0, remarks: '', selected_record: r };
  }

  returnBook(): void {
    const rf = this.returnForm;
    if (!rf.selected_record) return;
    const rec = rf.selected_record;

    let returnRec: any = Object.assign({}, rf.selected_record);
    returnRec.status = 'Returned';
    returnRec.return_date = rf.return_date;
    returnRec.fine_amount = rf.fine_amount;
    returnRec.fine_paid = rf.fine_paid;
    returnRec.remarks = rf.remarks;

    this.doUpdate('lib_issue_rec', returnRec);

    this.records.update(l => l.map(r => r.id === rec.id
      ? { ...r, status: 'Returned', return_date: rf.return_date, fine_amount: rf.fine_amount, fine_paid: rf.fine_paid, remarks: rf.remarks }
      : r
    ));
    this.books.update(l => l.map(b => b.id === rec.book_id
      ? { ...b, copies_available: b.copies_available + 1, status: 'Available' }
      : b
    ));
    this.members.update(l => l.map(m => m.id === rec.member_id
      ? { ...m, active_issues: Math.max(0, m.active_issues - 1), fine_pending: Math.max(0, m.fine_pending - (rf.fine_paid ? rf.fine_amount : 0)) }
      : m
    ));
    this.flash(`"${rec.book_title}" returned by ${rec.member_name}!`);
    this.returnForm = this.blankReturnForm(); this.returnSearch = '';
  }

  // ── MEMBER CRUD ────────────────────────────────────────
  openNewMember(): void {
    this.memberForm = { member_type:'Student', is_active:true, max_books:3, active_issues:0, fine_pending:0, membership_from: new Date().toISOString().substring(0,10) };
    this.editingMemberId.set(null); this.memberSubmitted = false;
    this.memberMode.set('form');
  }

  editMember(m: LibMember): void { 
    this.memberForm = {...m}; 
    this.memberForm.membership_from = this.memberForm.membership_from?.substr(0, 10);
    this.memberForm.membership_to = this.memberForm.membership_to?.substr(0, 10);
    this.editingMemberId.set(m.id); 
    this.memberSubmitted = false; 
    this.memberMode.set('form'); 
  }

  saveMember(): void {
    this.memberSubmitted = true;
    if (!this.memberForm.member_name || !this.memberForm.member_id) return;
    const mem: LibMember = {
      id:              this.editingMemberId() ?? this.nextMemberId++,
      member_id:       this.memberForm.member_id!,
      member_name:     this.memberForm.member_name!,
      member_type:     this.memberForm.member_type ?? 'Student',
      class_dept:      this.memberForm.class_dept ?? '',
      mobile:          this.memberForm.mobile ?? '',
      email:           this.memberForm.email ?? '',
      photo:           '',
      max_books:       this.memberForm.max_books ?? 3,
      active_issues:   this.memberForm.active_issues ?? 0,
      fine_pending:    this.memberForm.fine_pending ?? 0,
      membership_from: this.memberForm.membership_from ?? '',
      membership_to:   this.memberForm.membership_to ?? '',
      is_active:       this.memberForm.is_active ?? true,
    };
    if (this.editingMemberId() !== null) {
      this.members.update(l => l.map(m => m.id === mem.id ? mem : m));
      this.doUpdate('lib_members', mem);
    } else {
      this.members.update(l => [...l, mem]);
      this.doSave('lib_members', mem);
    }

    this.memberMode.set('list'); this.flash('Member saved!');
  }

  // ── DELETE ─────────────────────────────────────────────
  confirmDelete(type: string, id: number, label: string): void { this.deleteCtx.set({type,id,label}); this.showDeleteModal.set(true); }

  executeDelete(): void {
    const ctx = this.deleteCtx();
    if (!ctx) return;
    if (ctx.type === 'book') {
      this.doDelete('lib_books', ctx.id);
      this.books.update(l => l.filter(b => b.id !== ctx.id));
    }
    if (ctx.type === 'member') {
      this.doDelete('lib_members', ctx.id);
      this.members.update(l => l.filter(m => m.id !== ctx.id));
    }
    if (ctx.type === 'record') {
      this.doDelete('lib_issue_rec', ctx.id);
      this.flash('Deleted successfully.');
      this.records.update(l => l.filter(r => r.id !== ctx.id));
    }

    this.showDeleteModal.set(false); this.deleteCtx.set(null);
  }

  // ── HELPERS ───────────────────────────────────────────
  getViewBook():   Book | undefined      { return this.books().find(b => b.id === this.editingBookId()); }
  isOverdue(r: IssueRecord): boolean    { return r.status === 'Issued' && new Date(r.due_date) < new Date(); }
  daysOverdue(r: IssueRecord): number   { return Math.max(0, Math.ceil((new Date().getTime() - new Date(r.due_date).getTime()) / 86400000)); }
  calcFine(r: IssueRecord): number      { return this.daysOverdue(r) * r.fine_per_day; }

  getBookStatusClass(s: BookStatus): string   { return {Available:'bs-avail',Issued:'bs-issued',Reserved:'bs-reserved',Lost:'bs-lost',Damaged:'bs-damaged','Under Repair':'bs-repair'}[s]??''; }
  getIssueStatusClass(s: IssueStatus): string { return {Issued:'is-issued',Returned:'is-returned',Overdue:'is-overdue',Lost:'is-lost'}[s]??''; }
  getCategoryColor(cat: string): string {
    const map: Record<string,string> = { Fiction:'#7c3aed', Science:'#0284c7', Mathematics:'#1a3a6b', History:'#be185d', Technology:'#059669', Reference:'#d97706', Encyclopedia:'#ea580c' };
    return map[cat] ?? '#374151';
  }

  formatCurrency(n: number): string { return new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0}).format(n); }
  formatDate(s: string): string { if(!s) return '—'; return new Date(s).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); }
  getInitials(name: string): string { return name.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase(); }

  private flash(m: string): void { this.saveSuccess.set(m); setTimeout(()=>this.saveSuccess.set(''),2500); }

  blankIssueForm(): IssueForm {
    const today = new Date().toISOString().substring(0,10);
    const due   = new Date(); due.setDate(due.getDate()+14);
    return { member_search:'', book_search:'', selected_member:null, selected_book:null, issue_date:today, due_date:due.toISOString().substring(0,10), remarks:'' };
  }
  blankReturnForm(): ReturnForm { return { record_id:null, return_date:new Date().toISOString().substring(0,10), fine_amount:0, fine_paid:true, remarks:'', selected_record:null }; }


  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

}
